import {
  Text,
  Code,
  Icon,
  InlineStack,
  Label,
  FormattedDateTime,
  Spinner,
  Flex,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { SearchResult } from '@/services/auth/types';
import { useServicesQuery } from '@/services/api';
import { formatNumber } from '@/utils/number';
import { getUserDisplayName } from '@/services/auth/user-display-name';
import { CampaignStateLabels, ActionTypeLabels } from '@/features/audit-logs';
import { ACTION_TYPE_ICONS, CAMPAIGN_TYPE_ICONS } from '../constants';
import { MetadataRow } from './metadata-row';
import { CAMPAIGN_TYPE_LABELS } from './constants';

type Props = { result: Extract<SearchResult, { kind: 'campaign' }> };

const HIDDEN_ACTION_TYPES = new Set<string>([
  'conditional_branch_action',
  'multi_split_branch_action',
  'random_cohort_branch_action',
  'multi_lang_branch_action',
  'exit_action',
  'trigger',
]);

function groupActions(actions: Array<{ type: string }>): Array<{ type: string; count: number }> {
  const counts = new Map<string, number>();
  for (const action of actions) {
    if (HIDDEN_ACTION_TYPES.has(action.type)) continue;
    counts.set(action.type, (counts.get(action.type) ?? 0) + 1);
  }
  return Array.from(counts.entries()).map(([type, count]) => ({ type, count }));
}

const STATE_COLORS: Record<string, 'green' | 'red' | 'grey' | 'yellow'> = {
  draft: 'grey',
  running: 'green',
  stopped: 'red',
  archived: 'grey',
  sunsetting: 'red',
  stopping: 'red',
  archiving: 'grey',
  unarchiving: 'grey',
};

export function CampaignPreview({ result }: Props) {
  const envId = Number(result.other.env_id);
  const campaignId = result.other.id;

  const { data, isLoading } = useServicesQuery(
    'getCampaignById',
    { params: { environment_id: envId, campaign_id: campaignId } },
    { enabled: envId > 0 },
  );

  const campaign = data?.campaign;
  const tags = data?.tags;
  const actions = data?.actions as Array<{ type: string }> | undefined; // API types actions as unknown[]
  const actionGroups = actions != null ? groupActions(actions) : [];
  const createdByName = campaign?.created_by_user != null ? getUserDisplayName(campaign.created_by_user) : null;

  return (
    <>
      <MetadataRow label="Status">
        {isLoading ? (
          <Spinner />
        ) : campaign != null ? (
          <Label showStatusIndicator color={STATE_COLORS[campaign.state] ?? 'grey'}>
            {CampaignStateLabels[campaign.state] ?? campaign.state}
          </Label>
        ) : result.other.archived === 'true' ? (
          <Label showStatusIndicator color="grey">
            Archived
          </Label>
        ) : (
          <Text size="xs" color="subtle">
            —
          </Text>
        )}
      </MetadataRow>
      <MetadataRow label="ID">
        <Code size="xs">{result.other.id}</Code>
      </MetadataRow>
      <MetadataRow label="Type">
        <InlineStack gap="050" alignBlock="center">
          <Icon
            name={CAMPAIGN_TYPE_ICONS[result.other.campaign_type] ?? 'campaigns'}
            size="xs"
            sizeVersion="v2"
            color="subtle"
          />
          <Text size="xs">{CAMPAIGN_TYPE_LABELS[result.other.campaign_type] ?? 'Campaign'}</Text>
        </InlineStack>
      </MetadataRow>
      {actionGroups.length > 0 ? (
        <MetadataRow label="Actions">
          <InlineStack gap="100" alignBlock="center" wrap="wrap">
            {actionGroups.map((group) => (
              <Tooltip key={group.type} content={`${group.count} ${ActionTypeLabels[group.type] ?? group.type}`}>
                <InlineStack gap="050" alignBlock="center">
                  <Text size="xs">{group.count}</Text>
                  <Icon
                    name={ACTION_TYPE_ICONS[group.type as keyof typeof ACTION_TYPE_ICONS] ?? 'campaigns'}
                    size="xs"
                    sizeVersion="v2"
                    color="subtle"
                  />
                </InlineStack>
              </Tooltip>
            ))}
          </InlineStack>
        </MetadataRow>
      ) : null}
      {campaign != null && campaign.total_sent != null ? (
        <MetadataRow label="Sent">
          <Text size="xs">{formatNumber(campaign.total_sent)}</Text>
        </MetadataRow>
      ) : null}
      {tags != null && tags.length > 0 ? (
        <MetadataRow label="Tags">
          <Flex gap="050" wrap="wrap">
            {tags.map((tag) => (
              <Label key={tag.id} color="outline">
                {tag.name}
              </Label>
            ))}
          </Flex>
        </MetadataRow>
      ) : null}
      <MetadataRow label="Created">
        <Text size="xs">
          <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
        </Text>
      </MetadataRow>
      <MetadataRow label="Updated">
        <Text size="xs">
          <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
        </Text>
      </MetadataRow>
      {createdByName != null ? (
        <MetadataRow label="Created by">
          <Text size="xs" className="fs-mask">
            {createdByName}
          </Text>
        </MetadataRow>
      ) : null}
    </>
  );
}
